import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Settings (addendum §11) — visible to customers AND super-admin, separate from Admin.
 * A customer manages their workspace output defaults, FTP/SFTP export destination, and
 * output presets, and sees the template-building options their plan/edition allows
 * (resolved + capped by entitlements). The per-edition / per-customer template-options
 * EDITORS live in the admin Customers detail page. The AI prompt is server-side only.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { getTheme, setTheme, type ThemeChoice } from "../stores/theme.js";
import { useServices } from "../app/context.js";
import { useCachedResource } from "../stores/useCachedResource.js";
import { CACHE_KEYS } from "../stores/sessionWarmup.js";
import { getRenderBackend, setRenderBackend, type RenderBackend } from "../stores/renderBackend.js";
import { getPrimaryViewFallback, setPrimaryViewFallback } from "../stores/renderFallback.js";
import {
  getMockupMemBudgetMb,
  setMockupMemBudgetMb,
  getOutputBytesBudgetMb,
  setOutputBytesBudgetMb,
} from "../stores/memoryBudget.js";
import { isLossless, qualityWarning } from "./output.js";
import { capabilitySentence, compositorReadout, readoutSentence } from "./compositorReadout.js";
import type { CompositorInfo } from "@genz/engine-bind";
// WS-B B-10 / MIN-11: the five output fields + the preset row are ONE component now
// (`MARKETPLACE_PRESETS` moved with them into `OutputPresetButtons`).
import { OutputDefaultsFields, OutputPresetButtons } from "./OutputDefaultsFields.js";
import { ActionBar } from "../ui/ActionBar.js";
import { InfoHint } from "../ui/Tooltip.js";
import { LegacyConnectors } from "./LegacyConnectors.js";
import { SessionsPanel } from "./SessionsPanel.js";
import { HiggsfieldConnection } from "./HiggsfieldConnection.js";
import {
  getLocalOverrides, setLocalOverrides, clearLocalOverride, resolveWorkspaceSettings,
  OUTPUT_KEYS, type OutputKey, type SettingSource,
} from "./localSettings.js";
import type { ResolvedWorkspaceSettings, WorkspaceOutputSettings, WorkspaceSettings } from "@genz/platform";
import "./settings.css";
import { RowAction } from "../ui/RowAction.js";


/** What a BLANK port field means on save: the column's own default (`tenant_settings.ftp_port`
 *  is NOT NULL, default 21), never 0. */
const FTP_PORT_DEFAULT = 21;

/**
 * The CLOUD (tenant) fields this panel edits — the OUTPUT keys are device-local and are
 * tracked separately (`overrides`), so they are deliberately absent.
 *
 * EXPORTED for the suite (fix round 2, F-2/N-2). It started as the input to the "unsaved"
 * chip alone; F10 made it load-bearing for the Save's ENABLEMENT too, so a member dropped
 * from here without being dropped from `save()` strands a typed value behind a disabled
 * primary. `settings-panel.test.tsx` builds the expected PUT payload from this constant, so
 * the wire and the list can no longer drift apart in either direction.
 */
export const CLOUD_FIELDS = [
  "ftp_enabled", "ftp_protocol", "ftp_host", "ftp_port", "ftp_username", "ftp_remote_path",
] as const satisfies readonly (keyof WorkspaceSettings)[];

/** Whether the editable draft has drifted from the server value it was seeded from. Compared
 *  field-by-field (not by reference) because every re-poll hands back a fresh object. */
function cloudFieldsDiffer(draft: WorkspaceSettings | null, server: WorkspaceSettings | null): boolean {
  if (!draft || !server) return false; // nothing seeded yet → nothing to protect
  // `?? ""`: the server returns null for an unset text field while the input holds "".
  return CLOUD_FIELDS.some((k) => (draft[k] ?? "") !== (server[k] ?? ""));
}

/** Provenance chip label — customers see only "did I change this on THIS device or not". */
function sourceLabel(source: SettingSource): string {
  return source === "local" ? "Overridden on this device" : "Default";
}

/** Per-output-key provenance chip + a Reset control that appears only when overridden. */
function OutputMeta(props: {
  k: OutputKey;
  source: SettingSource;
  onReset: (key: OutputKey) => void | Promise<void>;
}) {
  const overridden = props.source === "local";
  return (
    <div className="setting-row__meta">
      <span className={`badge ${overridden ? "badge--warn" : ""}`} data-testid={`setting-${props.k}-source`}>
        {sourceLabel(props.source)}
      </span>
      {overridden && (
        <button type="button" className="btn btn--sm btn--ghost"
          data-testid={`setting-${props.k}-reset`}
          onClick={() => void props.onReset(props.k)}>
          Reset to default
        </button>
      )}
    </div>
  );
}

/**
 * WS-Z4 / rule 11 — the sub-nav's sections, in order.
 *
 * The first four are always present. The last two exist only when the workspace HAS
 * something to show — leftover connector credentials, or a cloud that answers
 * `/api/auth/sessions` — and each child reports that for itself, so a customer with neither
 * sees four entries rather than two dead ones.
 */
type SectionKey =
  | "appearance" | "rendering" | "output" | "ftp" | "generation" | "legacy" | "sessions";
export const BASE_SECTIONS: { key: SectionKey; label: string }[] = [
  { key: "appearance", label: "Appearance" },
  { key: "rendering", label: "Rendering" },
  { key: "output", label: "Output defaults" },
  { key: "ftp", label: "FTP / SFTP export" },
];

/**
 * WS-Z4 fix round 1, F10 — the sections the page's Save actually commits.
 *
 * `save()` writes exactly two things: the device-local OUTPUT overrides and the cloud FTP
 * block. Rule 7 puts the Save in the page-level `ActionBar`, so it sat — enabled, and sticky
 * — over the four sections that are not part of it. Appearance and Rendering apply the moment
 * a control is touched (`setTheme`, `setRenderBackend`, `setPrimaryViewFallback` and the two
 * memory budgets all write on change); Legacy and Sessions are lists whose own Delete /
 * Revoke buttons are the commit. A primary that commits nothing is a promise the page cannot
 * keep, so on those four it is disabled with the reason stated beside it — DISABLED rather
 * than hidden, so the bar does not change shape between sections and crux B4 still counts one
 * `.btn--primary` on this surface.
 */
const COMMITTABLE_SECTIONS: ReadonlySet<SectionKey> = new Set<SectionKey>(["output", "ftp"]);

export function SettingsPanel({ pageTitle = true }: { pageTitle?: boolean } = {}) {
  const [section, setSection] = useState<SectionKey>("appearance");
  /** Ties the "nothing to save here" sentence to the Save it explains (`aria-describedby`).
   *  `useId`, not a constant: the Editor tab mounts this same component. */
  const saveReasonId = useId();
  // How many rows each conditional child has. They fetch on mount whatever the open section
  // is, so the list is right on the first paint after their load resolves.
  const [legacyCount, setLegacyCount] = useState(0);
  const [sessionCount, setSessionCount] = useState(0);
  // WS-HF: whether this BUILD has a Higgsfield surface at all. False on web, so the entry is
  // absent there — a third section that exists only when there is something behind it.
  const [hfAvailable, setHfAvailable] = useState(false);
  const sections = useMemo(() => [
    ...BASE_SECTIONS,
    ...(hfAvailable ? [{ key: "generation" as const, label: "Image generation" }] : []),
    ...(legacyCount > 0 ? [{ key: "legacy" as const, label: "Legacy upload destinations" }] : []),
    ...(sessionCount > 0 ? [{ key: "sessions" as const, label: "Signed-in devices" }] : []),
  ], [hfAvailable, legacyCount, sessionCount]);
  // A section that stops existing (the last connector deleted, the last other device signed
  // out) must not leave the panel blank on a nav entry that is no longer in the list.
  useEffect(() => {
    if (!sections.some((x) => x.key === section)) setSection("appearance");
  }, [sections, section]);

  const [theme, setThemeState] = useState<ThemeChoice>(getTheme);
  const { client, io, engine } = useServices();
  // Cache-first (warmed at login): show the last-known settings/options INSTANTLY and
  // revalidate in the background, so a change we make "from our side" appears within ~60s
  // and the client never re-loads on each visit.
  const settingsRes = useCachedResource<ResolvedWorkspaceSettings>(CACHE_KEYS.settings, () => client.getSettings());
  const [s, setS] = useState<WorkspaceSettings | null>(settingsRes.data);
  // Device-local OUTPUT overrides (Wave-2 T4): the draft persisted to LocalStore on Save,
  // NEVER to the cloud. Loaded once on mount; edits mutate this map, not the cloud settings.
  const [overrides, setOverrides] = useState<Partial<WorkspaceOutputSettings>>({});
  // Render backend (per-device): auto = GPU warp + multi-core CPU compose when an adapter
  // exists; cpu = force everything onto the CPU (driver escape hatch). Applied at runtime.
  const [renderBackend, setRenderBackendState] = useState<RenderBackend>(getRenderBackend);
  // WS-H1 (AUD-3 IM-6): may a view with no local bake be composited over the primary view's
  // shell? Per-device, OFF by default, applied immediately — NOT part of the Save below.
  const [primaryViewFallback, setPrimaryViewFallbackState] = useState<boolean>(getPrimaryViewFallback);
  const [mockupMemBudget, setMockupMemBudgetState] = useState<number>(getMockupMemBudgetMb);
  const [outputBytesBudget, setOutputBytesBudgetState] = useState<number>(getOutputBytesBudgetMb);
  // WS-N / F-49: which BUILD this is. Diagnostics only — nothing about a render branches on it;
  // it selects which true sentence describes the Processor control (see the readout below).
  const isWeb = engine?.impl === "wasm";
  // "What does this machine HAVE" — adapter + thread count, from `backendInfo`.
  // (Both builds answer it since WS-N's N-2; on web the answer is `{cpu, 1, null}`, which is
  // true and which is exactly why `isWeb` has to pre-empt it in the sentence below.)
  const [backendInfo, setBackendInfo] = useState<{ backend: string; threads: number; gpuAdapter: string | null } | null>(null);
  useEffect(() => {
    // Optional-chained: web/WASM builds (and test mounts with a partial services mock)
    // have no engine.backendInfo — the descriptive fallback text stands.
    if (!engine?.backendInfo) return;
    let alive = true;
    void engine.backendInfo().then((b) => { if (alive) setBackendInfo(b); }).catch(() => { /* older desktop build */ });
    return () => { alive = false; };
  }, [engine, renderBackend]);
  // WS-S / AUD-8 LB-9(c): "what did the last render DO" — a DIFFERENT question from the one above,
  // and conflating the two is the whole defect. `backendInfo` answers "does an adapter exist and
  // does the preference allow it"; this answers "which path the last warp actually took, and why".
  // On a spot-UV varnish template they disagree, and the engine is right.
  const [compInfo, setCompInfo] = useState<CompositorInfo | null>(null);
  useEffect(() => {
    if (!engine?.compositorInfo) return;
    let alive = true;
    // Fetch on mount, on a backend toggle (`renderBackend` in the deps), and on tab focus —
    // the same cadence the editor's Compositor line uses (`MaskEditor.tsx`: open + `aiBusy`
    // flips), and deliberately NOT a poll: a render can only happen while the app has focus,
    // and an interval in a settings panel is a cost with no reader.
    const refresh = () => {
      void engine.compositorInfo?.().then((c) => { if (alive) setCompInfo(c); })
        .catch(() => { /* web / older desktop build without the command */ });
    };
    refresh();
    window.addEventListener("focus", refresh);
    return () => { alive = false; window.removeEventListener("focus", refresh); };
  }, [engine, renderBackend]);
  const [pw, setPw] = useState("");
  /** Whether an OUTPUT override has been edited and not yet written to LocalStore. The cloud
   *  half has `dirty`; this is the device-local half, and together they are what keeps the
   *  Save live on a section that cannot itself commit (F10). */
  const [outputPending, setOutputPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /**
   * The last server value this draft was seeded from (house `serverSeen` pattern — see
   * `admin/detail-shared.tsx`, `branding/BrandsPanel.tsx`). Everything about the freeze is
   * derived from comparing the draft to THIS, never from a latch.
   */
  const serverSeen = useRef<WorkspaceSettings | null>(settingsRes.data);

  /**
   * Whether the CLOUD draft (FTP block) differs from the server value we seeded it from.
   * `useCachedResource` re-polls every 60s, and the seeding effect below used to blindly
   * re-seed the draft from whatever came back — so a half-typed host/username/port was
   * silently reverted mid-edit (and a Save then wrote the stale values back). While this is
   * true the background refresh is not allowed to touch the draft.
   *
   * It was a BOOLEAN LATCH — set by any keystroke, cleared only by a successful save — so
   * typing a character and undoing it by hand froze the panel for the rest of its lifetime
   * and it silently stopped showing changes made on another device. A pending PASSWORD
   * counts as unsaved too: it lives in `pw`, not in the draft, so a comparison alone would
   * miss it.
   */
  const dirty = pw !== "" || cloudFieldsDiffer(s, serverSeen.current);

  /**
   * May the page's Save be pressed right now? (F10.)
   *
   * On the two sections it writes, always. Everywhere else only while there IS something
   * unsaved — an Output override typed on one section and then navigated away from, or a
   * half-edited FTP block — because the Save is the only thing that can commit it and a
   * disabled primary must never strand work. `dirty` is the cloud half, `outputPending` the
   * device-local half.
   */
  const canSave = COMMITTABLE_SECTIONS.has(section) || dirty || outputPending;

  // Seed/refresh the editable draft from the (cache-first, then revalidated) settings —
  // but NEVER over an unsaved edit (see `dirty`). `serverSeen` advances with every seed, so
  // "unsaved" always means "differs from the newest value the server actually gave us".
  useEffect(() => {
    if (!settingsRes.data) return;
    if (dirty) return;
    serverSeen.current = settingsRes.data;
    setS(settingsRes.data);
  }, [settingsRes.data, dirty]);
  // Hydrate the persisted device-local overrides so the output section opens already-resolved.
  useEffect(() => { void getLocalOverrides(io?.localStore).then(setOverrides); }, [io]);

  // The resolved OUTPUT values + per-key provenance: device-local → tenant → global → default.
  const resolved = useMemo(() => resolveWorkspaceSettings(settingsRes.data, overrides), [settingsRes.data, overrides]);

  // The port field's OWN text while it is blank. `null` = mirror the draft's number. A number
  // input reads "" as `Number("") === 0`, and 0 went to the wire — the cloud refuses it now
  // (`ftp_port` is 1..65535; port 0 is not dialable and the desktop types it as a `u16`), so a
  // blank field means the column's default, 21, and the draft carries that number while the
  // box itself stays empty and says so in its placeholder.
  const [portBlank, setPortBlank] = useState(false);

  // Edit a CLOUD (FTP/appearance) field on the draft.
  function set<K extends keyof WorkspaceSettings>(key: K, value: WorkspaceSettings[K]) {
    setS((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaved(false); // `dirty` is derived from the draft vs. `serverSeen` — no latch to set
  }

  // Edit an OUTPUT key as a DEVICE-LOCAL override (the value becomes source "local").
  function setOverride<K extends OutputKey>(key: K, value: WorkspaceOutputSettings[K]) {
    setOverrides((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
    setOutputPending(true); // the map is not on disk until Save writes it
  }

  // Clear one device-local override → the key reverts to the tenant/global/default value.
  async function resetOverride(key: OutputKey) {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSaved(false);
    await clearLocalOverride(key, io?.localStore);
  }

  async function save() {
    if (!s) return;
    setBusy(true);
    setError(null);
    try {
      // OUTPUT keys are DEVICE-LOCAL: persist the whole override map to LocalStore (a key
      // absent from the draft is cleared). The cloud PUT below carries NO output keys.
      const patch: Partial<WorkspaceOutputSettings> = {};
      for (const key of OUTPUT_KEYS) {
        (patch as Record<string, unknown>)[key] = (overrides as Record<string, unknown>)[key];
      }
      await setLocalOverrides(patch, io?.localStore);
      // FTP destination stays a per-tenant cloud setting.
      const updated = await client.updateSettings({
        ftp_enabled: s.ftp_enabled, ftp_protocol: s.ftp_protocol,
        ftp_host: s.ftp_host ?? "", ftp_port: s.ftp_port, ftp_username: s.ftp_username ?? "",
        ftp_remote_path: s.ftp_remote_path ?? "",
        ...(pw ? { ftp_password: pw } : {}),
      });
      setPw("");
      settingsRes.set(updated); // push fresh value straight into the cache + state
      setS(updated);
      serverSeen.current = updated; // the draft IS the server's truth now → no longer dirty
      setOutputPending(false);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!s) {
    return <p className="faint" data-testid="settings-loading">Loading settings…</p>;
  }

  return (
    <section className="settings" data-testid="settings-panel">
      {pageTitle && (
        <header className="panel-head">
          <div>
            <h1 className="panel-title">Settings</h1>
          </div>
        </header>
      )}
      {error && <p role="alert" data-testid="settings-error"><ErrorNotice detail={error} /></p>}

      <div className="settings__layout">
        {/* WS-Z4 / rule 11: the four (to six) `<h3>` sections were stacked down one column, so
            the FTP block sat four scrolls under the theme switch and the Save at the bottom
            was out of sight of everything it saves. The `.subnav` is the 200px left list the
            primitive exists for, and the panel shows ONE section at a time. Nothing is
            removed: every section is one click away and the two conditional ones appear in
            the list exactly when they have something to show. */}
        <nav className="subnav" aria-label="Settings sections" data-testid="settings-subnav">
          {sections.map((sec) => (
            <button
              key={sec.key}
              type="button"
              className="subnav__item"
              aria-current={section === sec.key ? "page" : undefined}
              data-testid={`settings-nav-${sec.key}`}
              onClick={() => setSection(sec.key)}
            >
              {sec.label}
            </button>
          ))}
        </nav>

        <div className="settings__section" data-testid={`settings-section-${section}`}>
          {section === "appearance" && (
            <>
              {/* D-25 / AUD-5 I-6 — the theme control DESIGN.md:255 has asked for since before
                  the audit. It sits where the Glass/Flat control was (K2-11 deleted that axis).
                  "System" is the default and REMOVES the attribute rather than resolving it, so
                  a macOS auto-appearance user still flips live at sunset. */}
              <h3>Appearance</h3>
              <div className="settings__grid" data-testid="settings-appearance">
                <label className="field">
                  <span>Theme</span>
                  <div className="seg" role="group" aria-label="Theme">
                    {(["system", "light", "dark"] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`seg__btn${theme === c ? " seg__btn--on" : ""}`}
                        aria-pressed={theme === c}
                        onClick={() => { setThemeState(c); setTheme(c); }}
                        data-testid={`theme-${c}`}
                      >
                        {c === "system" ? "System" : c === "light" ? "Light" : "Dark"}
                      </button>
                    ))}
                  </div>
                </label>
                <p className="caption">System follows your device&apos;s appearance setting.</p>
              </div>
            </>
          )}

          {section === "rendering" && (
            <>
              <h3>Rendering</h3>
              <div className="settings__grid" data-testid="settings-rendering">
                {/* WS-S / AUD-8 O-8 + LB-9(c). TWO modes, honestly labelled, and THREE lines
                    answering THREE different questions: what it does, what this machine has,
                    what the last render did. The old panel had one line trying to be all three,
                    keyed off `backendInfo` (availability) — so it printed "Using: GPU (…) for
                    image warping" before a single warp had run, and kept printing it on a
                    spot-UV varnish template where every warp ran on the CPU. Collapsing them
                    back into one is how that defect happened.

                    There is no third "GPU only" mode and there cannot be one: the CPU path is
                    not a fallback that can be switched off — it IS the compositor, and every
                    GPU failure mode already degrades to it. `RenderBackend` stays
                    `"auto" | "cpu"`. */}
                <label className="field">
                  <span>GPU acceleration</span>
                  <div className="seg" role="group" aria-label="GPU acceleration">
                    <button
                      type="button"
                      className={`seg__btn ${renderBackend === "auto" ? "seg__btn--on" : ""}`}
                      aria-pressed={renderBackend === "auto"}
                      disabled={isWeb}
                      onClick={() => { setRenderBackendState("auto"); if (engine) setRenderBackend(engine, "auto"); }}
                      data-testid="backend-auto"
                    >
                      On (when available)
                    </button>
                    <button
                      type="button"
                      className={`seg__btn ${renderBackend === "cpu" ? "seg__btn--on" : ""}`}
                      aria-pressed={renderBackend === "cpu"}
                      disabled={isWeb}
                      onClick={() => { setRenderBackendState("cpu"); if (engine) setRenderBackend(engine, "cpu"); }}
                      data-testid="backend-cpu"
                    >
                      Off
                    </button>
                  </div>
                </label>
                {/* 1. What it DOES. Measured: the GPU has exactly two consumers in the whole
                    engine (`render/warp.rs`'s two `gpu::available()` guards, wrapping
                    imageproc's `warp_into`), worth ~6 % of a 2000² render —
                    `perf_report.rs`'s `bench_compose_2000_gpu` (161.1 ms median min) vs
                    `bench_compose_2000_plain_cpu` (170.8 ms) on the identical fixture, M1 Pro
                    / Metal, five interleaved passes. A label promising co-processing over that
                    is the defect the owner reported.

                    These three lines are READOUTS, not hints: each is a live statement about
                    this machine and the last render, which is why they stay on the canvas
                    while the static explanations below became `InfoHint`s (rule 10). */}
                <p className="faint" data-testid="backend-scope">
                  Accelerates image warping only — compositing always uses all CPU cores. Templates with a
                  spot-UV varnish layer always render on the CPU so they look identical on every device.
                </p>
                {/* 2. What this machine HAS, from `backendInfo`. WS-I / IM-3: branch on what the
                    ENGINE reports, never on this panel's own toggle state — keying off
                    `renderBackend` is what kept a lying `backendInfo.backend` invisible. This
                    line cannot regress that way at all now: it is a pure function of
                    `backendInfo`, with no access to the control. */}
                <p className="faint" data-testid="backend-readout">
                  {/* WS-N / F-49: on web the Processor control has nothing to switch — the wasm
                      build is compiled without `accel` and without `gpu`
                      (scripts/build-wasm.sh), so both warping and compositing run on one thread
                      in the render Worker. The control used to sit there fully enabled above a
                      paragraph describing a desktop GPU, which is the "renders nothing,
                      explains nothing" half of F-49 on the surface an operator is most likely
                      to open. Since N-2 the web build ANSWERS `backendInfo` truthfully, which
                      is why this branch has to come FIRST: `{backend:"cpu", threads:1,
                      gpuAdapter:null}` is true, and run through `capabilitySentence` below it
                      prints "no compatible GPU adapter" — a hardware diagnosis for a browser. */}
                  {isWeb
                    ? "This is the browser build: rendering runs single-threaded in a Web Worker with no GPU acceleration. Install the desktop app to use your GPU and all CPU cores."
                    : backendInfo
                    ? capabilitySentence(backendInfo)
                    : "Auto uses any GPU (including integrated — Apple Silicon, Intel/AMD) for image warping plus all CPU cores for compositing. Off is the escape hatch for a misbehaving GPU driver."}
                </p>
                {/* 3. What the last render DID, from `compositor_info` — `lastPath` /
                    `lastFallbackReason`, never availability. Before any render this says so
                    plainly and does NOT say "GPU". */}
                <p className="faint" data-testid="compositor-readout">
                  {readoutSentence(compositorReadout(compInfo))}
                </p>
                <label className="field" data-testid="setting-primary-view-fallback">
                  <span>
                    Render a view with no baked assets over the primary view&apos;s shell{" "}
                    <span data-testid="primary-view-fallback-note">
                      <InfoHint
                        label="What happens when a view has no baked assets"
                        text="Off (recommended): the run stops and names the views to re-sync instead of exporting a plausible-looking wrong shell."
                      />
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={primaryViewFallback}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setPrimaryViewFallbackState(next);
                      setPrimaryViewFallback(next); // device-local, applied immediately (not part of Save)
                    }}
                  />
                </label>
                <label className="field" data-testid="setting-mockup-mem-budget">
                  <span>
                    Template artwork kept in memory (MB){" "}
                    <span data-testid="mockup-mem-budget-note">
                      <InfoHint
                        label="What the template-artwork memory budget does"
                        text="How much decrypted template artwork to keep in memory for fast re-renders. Anything evicted is re-read from local storage, never re-downloaded."
                      />
                    </span>
                  </span>
                  <input
                    type="number"
                    min={128}
                    max={4096}
                    step={128}
                    value={mockupMemBudget}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setMockupMemBudgetState(next);
                      setMockupMemBudgetMb(next); // device-local, applied immediately (not part of Save)
                    }}
                  />
                </label>
                <label className="field" data-testid="setting-output-bytes-budget">
                  <span>
                    Saved mock-ups kept in memory (MB){" "}
                    <span data-testid="output-bytes-budget-note">
                      <InfoHint
                        label="What the saved-mock-up memory budget does"
                        text="How many rendered mock-ups to keep in memory after they are saved. Saved files are re-read from the output folder when you export again."
                      />
                    </span>
                  </span>
                  <input
                    type="number"
                    min={64}
                    max={4096}
                    step={64}
                    value={outputBytesBudget}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setOutputBytesBudgetState(next);
                      setOutputBytesBudgetMb(next); // device-local, applied immediately (not part of Save)
                    }}
                  />
                </label>
              </div>
            </>
          )}

          {section === "output" && (
            <>
              {/* Rule 10: the "saved to this device only" paragraph is an `InfoHint` on the
                  heading it qualifies. The wrapper `<span>` carries the `data-testid` the
                  parity checklist and the suite key on — `InfoHint` takes no testid, and
                  `ui/Tooltip.tsx` belongs to another wave. */}
              <h3>
                Output defaults{" "}
                <span data-testid="output-local-note">
                  <InfoHint
                    label="Where output defaults are saved"
                    text="These are saved to this device only — they never sync to the cloud or to your other machines. Unchanged fields inherit the workspace / platform default."
                  />
                </span>
              </h3>
              {/* WS-B B-10 / MIN-11: the SAME five fields Admin → Workspace defaults renders,
                  from one component. What stays HERE is what is genuinely this page's own: the
                  local-override save path, the per-field source/reset affordance (`meta`), and
                  the "this device only" copy above. Editor → Settings is literally this same
                  component, so it is fixed by construction rather than by a third call site. */}
              <div data-testid="settings-output">
                <OutputDefaultsFields
                  testIdPrefix="out"
                  fieldTestIds
                  maxCanvasBlank={resolved.max_canvas_px.source === "default"}
                  values={{
                    output_width: resolved.output_width.value,
                    output_height: resolved.output_height.value,
                    output_format: resolved.output_format.value,
                    output_quality: resolved.output_quality.value,
                    max_canvas_px: resolved.max_canvas_px.value ?? null,
                    output_presets: resolved.output_presets.value,
                  }}
                  onChange={(k, v) => setOverride(k as never, v as never)}
                  meta={(k) => (
                    <OutputMeta
                      k={k as never}
                      source={(resolved as Record<string, { source: string }>)[k]!.source as never}
                      onReset={resetOverride}
                    />
                  )}
                  /* The two field-specific explanations that used to be hint PARAGRAPHS below
                     the whole grid. They went into `meta` first, which is the row BELOW the
                     control and below the source badge — so each rendered as a bare ⓘ with no
                     adjacent text (fix round 1, F9). `labelHint` puts them where every other
                     converted hint on this page sits: in the label they explain. */
                  labelHint={(k) => (
                    <>
                      {k === "max_canvas_px" && (
                        <span data-testid="max-canvas-note">
                          <InfoHint
                            label="What Max canvas does"
                            text="Hard ceiling for rendered output: every batch clamps its width and height to this, whichever preset is chosen. Leave blank to use the platform default."
                          />
                        </span>
                      )}
                      {k === "output_quality" && isLossless(resolved.output_format.value) && (
                        <span data-testid="lossless-note">
                          <InfoHint
                            label="Why Quality does not apply"
                            text="PNG is lossless — the quality value doesn’t apply (optimized compression)."
                          />
                        </span>
                      )}
                    </>
                  )}
                />
              </div>
              {/* Quality guardrails (spec 018 §0.4). A WARN BANNER, not a hint: it is an alert
                  about a value the operator just typed, and rule 9 keeps danger and warn on the
                  canvas at 720px while the explanations move into the ⓘ. */}
              {!isLossless(resolved.output_format.value) && qualityWarning(resolved.output_quality.value) && (
                <p className="banner banner--warn" role="alert" data-testid="quality-warning">
                  {qualityWarning(resolved.output_quality.value)}
                </p>
              )}
              <div className="settings__presets" data-testid="marketplace-presets"
                data-source={resolved.output_presets.source}>
                <OutputPresetButtons
                  presets={resolved.output_presets.value}
                  onAdd={(next) => setOverride("output_presets", next)}
                />
                <span className={`badge ${resolved.output_presets.source === "local" ? "badge--warn" : ""}`}
                  data-testid="setting-output_presets-source">
                  {sourceLabel(resolved.output_presets.source)}
                </span>
                {resolved.output_presets.source === "local" && (
                  <button type="button" className="btn btn--sm btn--ghost"
                    data-testid="setting-output_presets-reset"
                    onClick={() => void resetOverride("output_presets")}>
                    Reset to default
                  </button>
                )}
              </div>
              {resolved.output_presets.value.length > 0 && (
                <ul className="settings__preset-list" data-testid="output-presets">
                  {resolved.output_presets.value.map((p, i) => (
                    <li key={`${p.name}-${i}`} data-testid={`output-preset-${i}`}>
                      <span className="connectors__name">{p.name}</span>
                      <span className="faint">{p.width}×{p.height} · {p.format} · q{p.quality}</span>
                      <RowAction
                        label={`Delete preset ${p.name}`}
                        icon="trash"
                        tone="danger"
                        data-testid={`preset-delete-${i}`}
                        onClick={() => setOverride("output_presets",
                          resolved.output_presets.value.filter((_, j) => j !== i))}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {section === "ftp" && (
            <>
              {/* The "Upload destinations" (storage connectors) block used to sit below this
                  section, and this heading carried a "legacy" badge pointing operators at it.
                  BOTH were removed: no exporter ever read a connector — `getConnectorSecret`
                  and the Dropbox transport had zero callers, and the desktop transport still
                  does not speak SFTP (apps/desktop/src-tauri/src/ftp.rs: "ftps/sftp not yet
                  supported"), which is exactly the private-key auth the notice promised. Full
                  CRUD over encrypted credentials that can never ship a byte is decoration, and
                  the notice actively steered operators away from the ONE destination that
                  works. Re-add it together with a transport that actually uploads. */}
              <h3>
                FTP / SFTP export{" "}
                <span data-testid="ftp-note">
                  <InfoHint
                    label="What FTP / SFTP export does"
                    text={'The desktop batch export ("Export to server" on the Batch tab) pushes finished mock-ups straight from this machine to this server. Pixels never travel through the cloud.'}
                  />
                </span>
              </h3>
              <div className="settings__grid" data-testid="settings-ftp">
                <label className="field field--check">
                  <input type="checkbox" data-testid="ftp-enabled" checked={s.ftp_enabled}
                    onChange={(e) => set("ftp_enabled", e.target.checked)} /> <span>Enabled</span></label>
                <label className="field"><span>Protocol</span>
                  <select data-testid="ftp-protocol" value={s.ftp_protocol}
                    onChange={(e) => set("ftp_protocol", e.target.value)}>
                    <option value="ftp">FTP</option><option value="sftp">SFTP</option>
                  </select></label>
                <label className="field"><span>Host</span>
                  <input type="text" data-testid="ftp-host" value={s.ftp_host ?? ""}
                    onChange={(e) => set("ftp_host", e.target.value)} /></label>
                <label className="field"><span>Port</span>
                  <input type="number" data-testid="ftp-port" value={portBlank ? "" : s.ftp_port}
                    min={1} max={65535} step={1}
                    placeholder={`${FTP_PORT_DEFAULT} (default)`}
                    onChange={(e) => {
                      const text = e.target.value;
                      setPortBlank(text === "");
                      set("ftp_port", text === "" ? FTP_PORT_DEFAULT : Number(text));
                    }} /></label>
                <label className="field"><span>Username</span>
                  <input type="text" data-testid="ftp-username" value={s.ftp_username ?? ""}
                    onChange={(e) => set("ftp_username", e.target.value)} /></label>
                <label className="field"><span>Password {s.ftp_password_configured && (
                  <span className="badge badge--success" data-testid="ftp-pw-set">set</span>)}</span>
                  <input type="password" data-testid="ftp-password" value={pw}
                    placeholder={s.ftp_password_configured ? "•••••• (unchanged)" : ""}
                    onChange={(e) => setPw(e.target.value)} /></label>
                <label className="field"><span>Remote path</span>
                  <input type="text" data-testid="ftp-path" value={s.ftp_remote_path ?? ""}
                    onChange={(e) => set("ftp_remote_path", e.target.value)} /></label>
              </div>
            </>
          )}

          {/* WS-HF: connect the operator's OWN Higgsfield subscription and pick which engine a
              run uses. DESKTOP ONLY — on web it reports `available: false` and the sub-nav
              entry never appears. Same mount-always, draw-when-open contract as the two
              blocks below. */}
          <HiggsfieldConnection active={section === "generation"} onAvailable={setHfAvailable} client={client} />

          {/* Renders nothing unless leftover connector credentials actually exist — a cleanup
              affordance for data the removed Upload-destinations screen left behind, not a
              feature. See LegacyConnectors.tsx. Mounted on every section so it can report
              whether it HAS anything (which is what puts its entry in the sub-nav); it draws
              only when its own section is the open one. */}
          <LegacyConnectors active={section === "legacy"} onRowCount={setLegacyCount} />

          {/* WS-Q Q-5: every device signed in to this account, each individually revocable.
              Renders nothing on a cloud without `/api/auth/sessions`. Same mount-always,
              draw-when-open contract as the block above. */}
          <SessionsPanel active={section === "sessions"} onRowCount={setSessionCount} />
        </div>
      </div>

      {/* Rule 7: the page's commit, and its ONE primary, at the bottom of the column — not
          wherever the section that happens to be open ends. Save writes the device-local
          output overrides AND the cloud FTP block, so it belongs to the page, not to a
          section. Stickiness is NOT this page's business: `.actionbar` is
          `position: sticky; bottom: 0` in the shared layer (Z0 fix round 1, F-12), and the
          scrollport reserves `--actionbar-clearance-pad + --actionbar-h` under it (Z0 fix
          rounds 2 and 3, N-1 / P-5), so the last row of the open section stays reachable —
          which is what G-26 and G-54m measure. This page writes no `position` and no
          `scroll-padding` of its own. */}
      <ActionBar label="Settings actions" primary={
        <button type="button" className="btn btn--primary" disabled={busy || !canSave}
          aria-describedby={canSave ? undefined : saveReasonId}
          data-testid="settings-save" onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
      }>
        {/* ONE line: a second line makes the bar taller than `--actionbar-h` (G-59m). What
            Save writes is said where it is live — on Output defaults and FTP / SFTP. */}
        {!canSave && (
          <span className="faint" id={saveReasonId} data-testid="settings-save-na">
            Nothing to save here — it applies as you go. Save writes Output defaults and FTP / SFTP.
          </span>
        )}
        {saved && <span className="faint" data-testid="settings-saved">Saved.</span>}
        {dirty && !saved && (
          <span className="faint" role="status" data-testid="settings-dirty">
            Unsaved changes — background refresh paused so nothing overwrites them.
          </span>
        )}
      </ActionBar>
    </section>
  );
}
